import { Dispatch, SetStateAction } from 'react';

import { postOnboarding } from '#/api/account';

import { prepareErrorMessage } from '#/features/wallet/ethereum';
import {
  StarknetChainConfigurable,
  WalletState,
} from '#/features/wallet/wallet-context';
import StarknetWallet from '#/features/starknet-wallet/wallet';
import { buildOnboardingTypedData } from '#/features/wallet/chain-l2';
import EthereumWallet from '#/features/ethereum-wallet/wallet';

import { AsyncResult } from '#/utils/types';

export default function actionPerformOnboarding(
  ...params: Parameters<typeof performOnboarding>
) {
  return async () => performOnboarding(...params);
}

async function performOnboarding(
  walletState: WalletState,
  setState: Dispatch<SetStateAction<WalletState>>,
  options: StarknetChainConfigurable,
): AsyncResult<void> {
  if (walletState.isOnboarded) {
    return { ok: true, data: undefined };
  }

  const actions = prepareActions(setState);

  actions.resetError();
  actions.setStatus('signing_onboarding');

  try {
    const onboardingTypedData = buildOnboardingTypedData(
      options.starknetChainId,
    );
    const signature = StarknetWallet.signTypedData(onboardingTypedData);
    const starknetPublicKey = `0x${StarknetWallet.getPublicKey()}`;
    const starknetAddress = StarknetWallet.getAddress();

    const ethereumAddress = EthereumWallet.getAddress();

    const result = await postOnboarding({
      signature,
      starknetPublicKey,
      starknetAddress,
      ethereumAddress,
    });

    if (!result.ok) {
      const error = new Error(
        `Error sending Onboarding request: ${result.message}`,
        { cause: result.error },
      );
      actions.setError(error.message);
      actions.setStatus('wallet_connected_idle');
      return { ok: false, error };
    }
  } catch (err) {
    const description = 'Failed to perform onboarding';
    const { message, isException } = prepareErrorMessage(description, err);
    actions.setError(message);
    actions.setStatus('wallet_connected_idle');
    if (isException) {
      const error = new Error(description, { cause: err });
      return { ok: false, error };
    }
    return { ok: false, error: null };
  }

  actions.setIsOnboarded(true);
  actions.setStatus('wallet_connected_idle');
  return { ok: true, data: undefined };
}

function prepareActions(setState: Dispatch<SetStateAction<WalletState>>) {
  function setStatus(status: WalletState['step']) {
    setState((state) => ({ ...state, step: status }));
  }

  function setIsOnboarded(isOnboarded: WalletState['isOnboarded']) {
    setState((state) => ({ ...state, isOnboarded }));
  }

  const setError = (error: string) => {
    setState((state) => ({ ...state, error }));
  };

  const resetError = () => {
    setState((state) => ({ ...state, error: '' }));
  };

  return { setStatus, setIsOnboarded, setError, resetError };
}
